import { motion } from "framer-motion";
import { Gem, Play } from "lucide-react";
import { useState, useRef } from "react";

const galleryItems = [
  {
    id: 1,
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/ugnd4mmx_IMG_1950.jpeg",
    alt: "Custom blinged LA cap with black rhinestones",
    span: "col-span-1 row-span-2",
  },
  {
    id: 2,
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/v078l2zp_Custom%20Design%201.jpg",
    alt: "Custom fashion look with blinged shoes",
    span: "col-span-1 row-span-1",
  },
  {
    id: 3,
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/3c0nyuyp_Costum%20Design%202.jpg",
    alt: "Custom streetwear style with custom kicks",
    span: "col-span-1 row-span-1",
  },
  {
    id: 4,
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/b9micec6_IMG_8082.jpg",
    alt: "Stunning gold prom dress with ruffles",
    span: "col-span-1 row-span-2",
  },
  {
    id: 5,
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/nnkd6ir6_IMG_1772.jpeg",
    alt: "Custom purple prom dress with feathers",
    span: "col-span-1 row-span-1",
  },
  {
    id: 6,
    type: "video",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/gczgu7vl_904df802b7ab45cc8fd922e6062a77a7.MOV",
    alt: "Custom creation video 1",
    span: "col-span-1 row-span-1",
    label: "Custom Creation",
    color: "rose",
  },
  {
    id: 7,
    type: "video",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/k7f67q9q_00711863ce2c415ab1986ca2830ee83e.MOV",
    alt: "Custom creation video 2",
    span: "col-span-1 row-span-1",
    label: "Style Showcase",
    color: "gold",
  },
  {
    id: 8,
    type: "video",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/jj55r35w_IMG_2266.MOV",
    alt: "Behind the scenes video",
    span: "col-span-1 row-span-1",
    label: "Behind The Scenes",
    color: "rose",
  },
  {
    id: 9,
    type: "video",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/mc70bydq_539e2fd3ba2448d8aad4291a4dc3b5a4.mov",
    alt: "Custom cap showcase",
    span: "col-span-1 row-span-1",
    label: "Cap Showcase",
    color: "gold",
  },
  {
    id: 10,
    type: "video",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/kcjhl3ij_e266ebe55a184c49b7ec50b08a3e2bbc.MOV",
    alt: "Making of",
    span: "col-span-1 row-span-1",
    label: "Making Of",
    color: "rose",
  },
  {
    id: 11,
    type: "video",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/1lttoz15_RenderedVideo.MOV",
    alt: "Custom creations highlight reel",
    span: "col-span-1 row-span-1",
    label: "Highlight Reel",
    color: "gold",
  },
  {
    id: 12,
    type: "image",
    src: "https://customer-assets.emergentagent.com/job_threaded-events/artifacts/px4jtkg3_IMG_1951.jpeg",
    alt: "Custom blinged cap with crystal embellishments",
    span: "col-span-1 row-span-1",
  },
];

// Styled video card with label
const VideoCard = ({ item, isPlaying, onPlay, onClose }) => {
  const videoRef = useRef(null);
  
  const handlePlay = () => {
    onPlay();
  };

  const handleVideoEnd = () => {
    onClose();
  };

  const colorClasses = item.color === 'gold' 
    ? 'from-gold/20 to-gold/5 border-gold/30' 
    : 'from-rose/20 to-rose/5 border-rose/30';

  const buttonColor = item.color === 'gold' ? 'bg-gold' : 'bg-rose';
  const textColor = item.color === 'gold' ? 'text-gold' : 'text-rose';

  return (
    <div className="relative w-full h-full overflow-hidden">
      {isPlaying ? (
        <video
          ref={videoRef}
          autoPlay
          controls
          className="w-full h-full object-cover"
          onEnded={handleVideoEnd}
        >
          <source src={item.src} type="video/mp4" />
        </video>
      ) : (
        <div 
          className={`w-full h-full bg-gradient-to-br ${colorClasses} border flex flex-col items-center justify-center cursor-pointer hover:scale-[1.02] transition-transform`}
          onClick={handlePlay}
        >
          {/* Play button */}
          <div className={`w-20 h-20 rounded-full ${buttonColor} flex items-center justify-center mb-4 shadow-lg hover:scale-110 transition-transform`}>
            <Play className="w-10 h-10 text-black ml-1" fill="black" />
          </div>
          {/* Label */}
          <p className={`font-heading text-xl ${textColor}`}>{item.label}</p>
          <p className="text-zinc-500 text-sm mt-1">Click to play</p>
        </div>
      )}
    </div>
  );
};

const GallerySection = () => {
  const [playingVideo, setPlayingVideo] = useState(null);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 md:py-32 px-4 md:px-8 lg:px-16 bg-[#0A0A0A]">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16 md:mb-24"
        >
          <Gem className="w-8 h-8 text-gold mx-auto mb-4" />
          <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl mb-4">
            The <span className="text-gold">Gallery</span>
          </h2>
          <p className="text-zinc-400 text-lg max-w-2xl mx-auto">
            A glimpse into our handcrafted creations and unforgettable experiences
          </p>
        </motion.div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 auto-rows-[300px]">
          {galleryItems.map((item, index) => (
            <motion.div
              key={item.id}
              data-testid={`gallery-item-${item.id}`}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className={`gallery-item ${item.span} relative group cursor-pointer`}
            >
              {item.type === "video" ? (
                <VideoCard
                  item={item}
                  isPlaying={playingVideo === item.id}
                  onPlay={() => setPlayingVideo(item.id)}
                  onClose={() => setPlayingVideo(null)}
                />
              ) : (
                <>
                  <img
                    src={item.src}
                    alt={item.alt}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </>
              )}
              
              {/* Hover overlay */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-rose/50 transition-colors duration-300 pointer-events-none" />
            </motion.div>
          ))}
        </div>

        {/* Instagram CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <a
            href="https://www.instagram.com/dainalaneebrand/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-gallery-link"
            className="inline-flex items-center gap-3 text-rose hover:text-rose-light transition-colors group"
          >
            <i className="fab fa-instagram text-2xl" />
            <span className="uppercase tracking-widest text-sm">See More on Instagram</span>
            <span className="group-hover:translate-x-2 transition-transform">&rarr;</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default GallerySection;
